/**
 * Strip leaked HTML-like markup tags that no chat directive plugin claimed
 * (PLT-5092).
 *
 * Background: the chat surface deliberately ships a small set of HTML-like
 * inline directives — `<badge>`, `<cite>`, `<private />`, and the emphasis
 * synonyms `<b>` / `<strong>` / `<i>` / `<em>`. Each has a remark plugin
 * (`remarkToneBadges`, `remarkCitationPills`, `remarkLockBadges`,
 * `remarkInlineEmphasis`) that converts its tag into a real mdast node so it
 * renders as a chip / pill / emphasis span. react-markdown does NOT render
 * raw HTML and we run no `rehype-raw`, so any tag those plugins do NOT consume
 * survives as a leftover `html` mdast node and leaks raw to the DOM — the user
 * sees the literal `<bold>` / `<badge …>` characters as visible noise. This is
 * the same root cause documented in PLT-4806 / PLT-4896 / PLT-5054.
 *
 * Models occasionally emit tags outside the supported set (`<bold>` instead of
 * `<b>`, a `<section>` wrapper, a malformed unclosed `<badge …>` the badge
 * plugin couldn't pair) and those leak. This module is the last-line-of-defence
 * that removes them, keeping the inner text.
 *
 * Because it runs AFTER all the directive plugins (see the plugin order in
 * `upright-chat-markdown.tsx`), every well-formed supported directive has
 * already been turned into a non-`html` node by then. So any remaining `html`
 * node that is a *bare tag* (just an opening / closing / self-closing tag, no
 * other content) is by definition unhandled — safe to drop. For a paired tag
 * like `<bold>Lorem</bold>`, micromark splits it into three sibling nodes
 * (`html('<bold>')`, `text('Lorem')`, `html('</bold>')`); dropping the two
 * `html` nodes leaves the `text('Lorem')` node untouched, which is exactly the
 * `<bold>Lorem</bold>` → `Lorem` behaviour the issue asks for.
 *
 * Gating: applied only for non-team (external) users — team users keep seeing
 * the leaked tags so drift stays visible for debugging (PLT-5092). The gating
 * lives in `upright-chat-markdown.tsx` / `upright-chat.tsx`, not here; this
 * module is a pure transform so it can be unit-tested directly under
 * mocha+ts-node, matching the other `*-markers.ts` / `*-tags.ts` helpers in
 * this folder.
 */

/**
 * Minimal mdast node shape the transform needs to inspect: a `type`, an
 * optional `value` (`html` / `text` nodes), and optional `children`
 * (container nodes). Kept generic so the plugin can pass any mdast node
 * through without importing the full mdast types into the unit test.
 */
export interface StripReconNode {
  type: string;
  value?: string;
  children?: StripReconNode[];
}

/**
 * Matches a single bare HTML-like tag and nothing else: an opening
 * (`<name …>`), closing (`</name>`), or self-closing (`<name … />`) tag whose
 * whole (trimmed) string is just that one tag.
 *
 * - `[a-zA-Z][a-zA-Z0-9:_-]*` — a tag name starting with a letter. Covers the
 *   leaked shapes seen in practice (`bold`, `section`, `badge`) and the
 *   namespaced / hyphenated forms an LLM might invent.
 * - `(?:\s[^<>]*?)?` — optional attribute list (anything that isn't an angle
 *   bracket). Lazy so the trailing `\/?>` still anchors correctly.
 * - Anchored `^…$` so a node whose value carries surrounding text
 *   (`<div>content</div>` as one value) does NOT match — we only strip pure
 *   tag fragments and never silently swallow content.
 */
const BARE_TAG_RE = /^<\/?[a-zA-Z][a-zA-Z0-9:_-]*(?:\s[^<>]*?)?\/?>$/;

/** True when `value` is a single bare HTML-like tag (open / close / self-close). */
export function isStrippableTagHtmlValue(value: string): boolean {
  return BARE_TAG_RE.test(value.trim());
}

/**
 * Return a copy of `children` with every leftover bare-tag `html` node
 * removed, or `null` when nothing changed (so the remark plugin can skip the
 * reassignment). Only `html` nodes are considered — `text` nodes are never
 * touched, so prose like "use the <Enter> key" is left alone (it tokenises as
 * text, not html, and we deliberately don't strip math-like `a < b > c`
 * either).
 *
 * Pure function — no AST mutation — so it can be unit-tested directly.
 */
export function stripLeakedMarkupTagNodes(children: StripReconNode[]): StripReconNode[] | null {
  let changed = false;
  const out: StripReconNode[] = [];
  for (const node of children) {
    if (node.type === 'html' && typeof node.value === 'string' && isStrippableTagHtmlValue(node.value)) {
      changed = true;
      continue;
    }
    out.push(node);
  }
  return changed ? out : null;
}
